import { useState, useEffect } from 'react';
import { useApiKeysEnhanced, useCreateApiKey, useRevokeApiKey, useApiKeyStats } from '@/hooks/useApiKeys';
import { useSpaces } from '@/hooks/useSpaces';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Skeleton } from '@/components/ui';
import { useAuthStore } from '@/stores';
import { formatDate } from '@/lib/utils';
import { Key, Plus, Trash2, Copy, Check, AlertCircle, BarChart3, X, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import type { ApiKeyScope, ApiKey } from '@/types';

// localStorage key for storing API keys
const STORED_KEYS_KEY = 'stored_api_keys';

// Get stored API keys from localStorage
function getStoredApiKeys(): Record<number, string> {
  try {
    const stored = localStorage.getItem(STORED_KEYS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

// Save API key to localStorage
function saveApiKeyToStorage(keyId: number, keyValue: string): void {
  const stored = getStoredApiKeys();
  stored[keyId] = keyValue;
  localStorage.setItem(STORED_KEYS_KEY, JSON.stringify(stored));
}

// Remove API key from localStorage
function removeApiKeyFromStorage(keyId: number): void {
  const stored = getStoredApiKeys();
  delete stored[keyId];
  localStorage.setItem(STORED_KEYS_KEY, JSON.stringify(stored));
}

export function ApiKeys() {
  const { user } = useAuthStore();
  const { data: keys, isLoading } = useApiKeysEnhanced();
  const { data: spacesData } = useSpaces(1, 100);
  const createKey = useCreateApiKey();
  const revokeKey = useRevokeApiKey();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedKey, setSelectedKey] = useState<ApiKey | null>(null);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [storedKeys, setStoredKeys] = useState<Record<number, string>>({});

  // Load stored keys on mount
  useEffect(() => {
    setStoredKeys(getStoredApiKeys());
  }, []);

  // Create form state
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDescription, setNewKeyDescription] = useState('');
  const [newKeyScope, setNewKeyScope] = useState<ApiKeyScope>('read');
  const [newKeySpaces, setNewKeySpaces] = useState<number[]>([]);
  const [restrictSpaces, setRestrictSpaces] = useState(false);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('请输入 API Key 名称');
      return;
    }

    try {
      const result = await createKey.mutateAsync({
        name: newKeyName.trim(),
        description: newKeyDescription.trim() || undefined,
        scopes: newKeyScope,
        space_ids: restrictSpaces && newKeySpaces.length > 0 ? newKeySpaces : undefined,
      });
      setCreatedKey(result.key);
      // Save the key to localStorage for later retrieval
      if (result.id && result.key) {
        saveApiKeyToStorage(result.id, result.key);
        setStoredKeys(getStoredApiKeys());
      }
      // Reset form
      setNewKeyName('');
      setNewKeyDescription('');
      setNewKeyScope('read');
      setNewKeySpaces([]);
      setRestrictSpaces(false);
      setShowCreateForm(false);
      toast.success('API Key 创建成功');
    } catch (error: any) {
      toast.error(error.message || '创建失败');
    }
  };

  const handleRevokeKey = async (keyId: number, keyName: string) => {
    if (!confirm(`确定要吊销 API Key "${keyName}" 吗？此操作不可恢复。`)) {
      return;
    }

    try {
      await revokeKey.mutateAsync(keyId);
      // Remove from localStorage
      removeApiKeyFromStorage(keyId);
      setStoredKeys(getStoredApiKeys());
      toast.success('API Key 已吊销');
    } catch (error: any) {
      toast.error(error.message || '吊销失败');
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('已复制到剪贴板');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('复制失败');
    }
  };

  const toggleSpace = (spaceId: number) => {
    setNewKeySpaces(prev =>
      prev.includes(spaceId)
        ? prev.filter(id => id !== spaceId)
        : [...prev, spaceId]
    );
  };

  const getScopeLabel = (scope: ApiKeyScope) => {
    const labels: Record<ApiKeyScope, string> = {
      read: '只读',
      write: '读写',
      admin: '管理员',
    };
    return labels[scope];
  };

  const getScopeColor = (scope: ApiKeyScope) => {
    const colors: Record<ApiKeyScope, string> = {
      read: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      write: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      admin: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    };
    return colors[scope];
  };

  if (!user) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">请先登录</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Key className="h-6 w-6" />
            API Keys
          </h1>
          <p className="text-muted-foreground mt-1">
            管理 API 访问密钥，用于程序化访问 Wiki API
          </p>
        </div>
        <Button onClick={() => setShowCreateForm(true)} disabled={showCreateForm}>
          <Plus className="h-4 w-4 mr-2" />
          创建 Key
        </Button>
      </div>

      {/* Created key display */}
      {createdKey && (
        <Card className="border-green-500 bg-green-50 dark:bg-green-950">
          <CardHeader>
            <CardTitle className="text-green-700 dark:text-green-400 flex items-center gap-2">
              <Check className="h-5 w-5" />
              API Key 创建成功
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-green-600 dark:text-green-300 mb-3">
              请立即复制保存此密钥，关闭后将无法再次查看完整密钥！
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 p-3 bg-white dark:bg-gray-900 rounded border font-mono text-sm break-all">
                {createdKey}
              </code>
              <Button
                variant="outline"
                onClick={() => copyToClipboard(createdKey)}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <Button
              variant="ghost"
              className="mt-3"
              onClick={() => setCreatedKey(null)}
            >
              关闭
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create form */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>创建新 API Key</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">名称 *</label>
              <Input
                placeholder="例如：CI/CD 自动化"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                maxLength={100}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">描述</label>
              <Input
                placeholder="可选的描述信息"
                value={newKeyDescription}
                onChange={(e) => setNewKeyDescription(e.target.value)}
                maxLength={500}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">权限范围</label>
              <select
                className="w-full px-3 py-2 border rounded-md bg-background"
                value={newKeyScope}
                onChange={(e) => setNewKeyScope(e.target.value as ApiKeyScope)}
              >
                <option value="read">只读 - 只能读取数据</option>
                <option value="write">读写 - 可以创建和编辑内容</option>
                {user.role === 'admin' && (
                  <option value="admin">管理员 - 完全访问权限</option>
                )}
              </select>
            </div>

            {/* Space restriction */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="restrictSpaces"
                  checked={restrictSpaces}
                  onChange={(e) => {
                    setRestrictSpaces(e.target.checked);
                    if (!e.target.checked) {
                      setNewKeySpaces([]);
                    }
                  }}
                  className="rounded"
                />
                <label htmlFor="restrictSpaces" className="text-sm font-medium cursor-pointer">
                  限制访问特定空间
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                启用后，此 API Key 只能访问选定的空间。不启用则可访问所有空间。
              </p>
              {restrictSpaces && spacesData?.items && (
                <div className="space-y-2 pt-2">
                  <label className="block text-sm font-medium">选择空间</label>
                  <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1">
                    {spacesData.items.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-2">暂无可用空间</p>
                    ) : (
                      spacesData.items.map((space) => (
                        <label
                          key={space.id}
                          className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={newKeySpaces.includes(space.id)}
                            onChange={() => toggleSpace(space.id)}
                            className="rounded"
                          />
                          <span className="text-sm">{space.name}</span>
                          <span className="text-xs text-muted-foreground">({space.slug})</span>
                        </label>
                      ))
                    )}
                  </div>
                  {newKeySpaces.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      已选择 {newKeySpaces.length} 个空间
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button onClick={handleCreateKey} disabled={createKey.isPending}>
                {createKey.isPending ? '创建中...' : '创建'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewKeyName('');
                  setNewKeyDescription('');
                  setNewKeyScope('read');
                  setNewKeySpaces([]);
                  setRestrictSpaces(false);
                }}
              >
                取消
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key list */}
      <Card>
        <CardHeader>
          <CardTitle>我的 API Keys</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : keys && keys.length > 0 ? (
            <div className="space-y-3">
              {keys.map((key) => (
                <KeyCard
                  key={key.id}
                  apiKey={key}
                  onRevoke={() => handleRevokeKey(key.id, key.name)}
                  onViewStats={() => setSelectedKey(key)}
                  isRevoking={revokeKey.isPending}
                  getScopeLabel={getScopeLabel}
                  getScopeColor={getScopeColor}
                  storedKey={storedKeys[key.id]}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Key className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>暂无 API Key</p>
              <p className="text-sm mt-1">点击上方按钮创建第一个 API Key</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">使用说明</CardTitle>
        </CardHeader>
        <CardContent className="prose prose-sm dark:prose-invert">
          <p>使用 API Key 访问 Wiki API：</p>
          <pre className="bg-muted p-3 rounded text-sm overflow-x-auto">
{`curl -H "Authorization: Bearer ww_your_api_key" \\
  https://your-wiki.com/api/v1/pages`}
          </pre>
          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-950 rounded border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
              <div className="text-sm text-yellow-800 dark:text-yellow-200">
                <p className="font-medium">安全提示</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>API Key 保存在浏览器本地存储中，清除浏览器数据后需要重新获取</li>
                  <li>不要在客户端代码或公开仓库中暴露 API Key</li>
                  <li>定期轮换 API Key，及时吊销不再使用的 Key</li>
                  <li>为不同用途创建不同的 Key，便于管理和追踪</li>
                  <li>限制空间访问可以最小化 API Key 的权限范围</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <a href="/api-docs" className="text-primary hover:underline">
              查看完整 API 文档 →
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Stats Modal */}
      {selectedKey && (
        <ApiKeyStatsModal
          apiKey={selectedKey}
          onClose={() => setSelectedKey(null)}
          getScopeLabel={getScopeLabel}
          getScopeColor={getScopeColor}
        />
      )}
    </div>
  );
}

// Key Card Component
interface KeyCardProps {
  apiKey: ApiKey;
  onRevoke: () => void;
  onViewStats: () => void;
  isRevoking: boolean;
  getScopeLabel: (scope: ApiKeyScope) => string;
  getScopeColor: (scope: ApiKeyScope) => string;
  storedKey?: string;
}

function KeyCard({ apiKey, onRevoke, onViewStats, isRevoking, getScopeLabel, getScopeColor, storedKey }: KeyCardProps) {
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('已复制到剪贴板');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('复制失败');
    }
  };

  const fullKey = storedKey || apiKey.key;
  const displayKey = showKey && fullKey ? fullKey : `${apiKey.prefix}****************`;

  return (
    <div className="border rounded-lg p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium">{apiKey.name}</span>
          <span className={`px-2 py-0.5 text-xs rounded ${getScopeColor(apiKey.scopes)}`}>
            {getScopeLabel(apiKey.scopes)}
          </span>
          {!apiKey.is_active && (
            <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              已吊销
            </span>
          )}
          {apiKey.restrict_to_spaces && (
            <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
              受限空间
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onViewStats}>
            <BarChart3 className="h-4 w-4 mr-1" />
            统计
          </Button>
          {apiKey.is_active && (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700"
              onClick={onRevoke}
              disabled={isRevoking}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              吊销
            </Button>
          )}
        </div>
      </div>
      <div className="text-sm text-muted-foreground flex items-center gap-2">
        <code className="bg-muted px-1 rounded font-mono">{displayKey}</code>
        {fullKey && apiKey.is_active && (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2"
              onClick={() => setShowKey(!showKey)}
              title={showKey ? '隐藏' : '显示'}
            >
              {showKey ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2"
              onClick={() => copyToClipboard(fullKey)}
              title="复制"
            >
              {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
            </Button>
          </>
        )}
        <span className="mx-2">·</span>
        <span>限流: {apiKey.rate_limit}/小时</span>
        {apiKey.expires_at && (
          <>
            <span className="mx-2">·</span>
            <span>过期: {formatDate(apiKey.expires_at)}</span>
          </>
        )}
      </div>
      {apiKey.description && (
        <p className="text-sm text-muted-foreground">{apiKey.description}</p>
      )}
      {apiKey.restrict_to_spaces && apiKey.spaces && apiKey.spaces.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {apiKey.spaces.map((space) => (
            <span
              key={space.id}
              className="px-2 py-0.5 text-xs rounded bg-muted"
            >
              {space.name}
            </span>
          ))}
        </div>
      )}
      <div className="text-xs text-muted-foreground">
        创建于 {formatDate(apiKey.created_at)}
        {apiKey.last_used_at && (
          <span> · 最后使用 {formatDate(apiKey.last_used_at)}</span>
        )}
      </div>
    </div>
  );
}

// Stats Modal Component
interface ApiKeyStatsModalProps {
  apiKey: ApiKey;
  onClose: () => void;
  getScopeLabel: (scope: ApiKeyScope) => string;
  getScopeColor: (scope: ApiKeyScope) => string;
}

function ApiKeyStatsModal({ apiKey, onClose, getScopeLabel, getScopeColor }: ApiKeyStatsModalProps) {
  const { data: statsData, isLoading } = useApiKeyStats(apiKey.id, 7);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-background">
          <CardTitle>{apiKey.name} - 使用统计</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Basic info */}
          <div>
            <h3 className="font-medium mb-2">基本信息</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">前缀：</span>
                <code className="bg-muted px-1 rounded">{apiKey.prefix}****************</code>
              </div>
              <div>
                <span className="text-muted-foreground">权限范围：</span>
                <span className={`px-2 py-0.5 text-xs rounded ${getScopeColor(apiKey.scopes)}`}>
                  {getScopeLabel(apiKey.scopes)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">限流：</span>
                {apiKey.rate_limit} 次/小时
              </div>
              <div>
                <span className="text-muted-foreground">状态：</span>
                {apiKey.is_active ? (
                  <span className="text-green-600">活跃</span>
                ) : (
                  <span className="text-red-600">已吊销</span>
                )}
              </div>
              {apiKey.description && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">描述：</span>
                  {apiKey.description}
                </div>
              )}
            </div>
          </div>

          {/* Space permissions */}
          <div>
            <h3 className="font-medium mb-2">空间权限</h3>
            {apiKey.restrict_to_spaces && apiKey.spaces ? (
              <div className="flex flex-wrap gap-2">
                {apiKey.spaces.map((space) => (
                  <span key={space.id} className="px-2 py-1 bg-muted rounded text-sm">
                    {space.name}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">可访问所有空间</p>
            )}
          </div>

          {/* Usage statistics */}
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : statsData ? (
            <div>
              <h3 className="font-medium mb-2">使用统计 (最近7天)</h3>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-4 bg-muted rounded">
                  <p className="text-sm text-muted-foreground">总请求数</p>
                  <p className="text-2xl font-bold">{statsData.overview.total_requests.toLocaleString()}</p>
                </div>
                <div className="p-4 bg-muted rounded">
                  <p className="text-sm text-muted-foreground">错误数</p>
                  <p className={`text-2xl font-bold ${statsData.overview.total_errors > 0 ? 'text-red-500' : ''}`}>
                    {statsData.overview.total_errors.toLocaleString()}
                  </p>
                </div>
                <div className="p-4 bg-muted rounded">
                  <p className="text-sm text-muted-foreground">平均延迟</p>
                  <p className="text-2xl font-bold">
                    {statsData.overview.avg_latency_ms ? Math.round(statsData.overview.avg_latency_ms) : '-'}ms
                  </p>
                </div>
              </div>

              {/* Chart */}
              {statsData.stats.length > 0 ? (
                <div>
                  <h4 className="text-sm font-medium mb-2">请求趋势</h4>
                  <UsageChart data={statsData.stats} />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">暂无使用数据</p>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

// Simple usage chart component
interface UsageChartProps {
  data: { hour: string; request_count: number; error_count: number }[];
}

function UsageChart({ data }: UsageChartProps) {
  const maxCount = Math.max(...data.map(d => d.request_count), 1);

  return (
    <div className="space-y-1">
      <div className="h-24 flex items-end gap-0.5">
        {data.map((d, i) => (
          <div
            key={i}
            className="flex-1 bg-primary/20 hover:bg-primary/40 rounded-t transition-colors relative group cursor-pointer"
            style={{ height: `${(d.request_count / maxCount) * 100}%` }}
          >
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-popover text-popover-foreground text-xs rounded shadow opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
              <div>{d.hour.slice(0, 10)} {d.hour.slice(11, 13)}:00</div>
              <div>请求: {d.request_count}</div>
              {d.error_count > 0 && <div className="text-red-500">错误: {d.error_count}</div>}
            </div>
            {/* Error indicator */}
            {d.error_count > 0 && (
              <div
                className="absolute bottom-0 left-0 right-0 bg-red-500/50 rounded-b"
                style={{ height: `${(d.error_count / d.request_count) * 100}%` }}
              />
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{data[0]?.hour.slice(0, 10) || ''}</span>
        <span>{data[data.length - 1]?.hour.slice(0, 10) || ''}</span>
      </div>
    </div>
  );
}

export default ApiKeys;
